import { useEffect, useState } from 'react';
import api from '../../api';

export default function ProfilePage() {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ display_name: '', locale: '', timezone: '' });
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [changingPw, setChangingPw] = useState(false);
  const [message, setMessage] = useState('');

  const load = async () => {
    try {
      const res = await api.get('/api/auth/me');
      setProfile(res.data);
      setForm({
        display_name: res.data.display_name || '',
        locale: res.data.locale || 'en',
        timezone: res.data.timezone || 'UTC',
      });
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const saveProfile = async () => {
    setSavingProfile(true);
    setMessage('');
    try {
      const res = await api.put('/api/user/profile', form);
      setProfile(res.data);
      setMessage('Profile updated.');
    } catch (err) {
      console.error(err);
      setMessage('Failed to update profile.');
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async () => {
    setChangingPw(true);
    setMessage('');
    try {
      await api.put('/api/user/password', pwForm);
      setPwForm({ currentPassword: '', newPassword: '' });
      setMessage('Password changed successfully.');
    } catch (err) {
      console.error(err);
      setMessage('Failed to change password.');
    } finally {
      setChangingPw(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 flex items-start justify-center py-8">
      <div className="w-full max-w-3xl space-y-6">
        <header>
          <div className="text-xs uppercase tracking-[0.2em] text-cyan-400/80 mb-1">
            Account Settings
          </div>
          <div className="text-lg font-semibold">Profile & Security</div>
        </header>

        {message && (
          <div className="text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/50 rounded-lg px-3 py-2">
            {message}
          </div>
        )}

        {/* Profile */}
        <section className="border border-slate-800 rounded-2xl bg-slate-900/70 px-4 py-4 space-y-3">
          <div className="text-sm font-medium mb-1">Profile</div>
          <div className="space-y-2 text-sm">
            <Field label="Display name">
              <input
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-400"
                value={form.display_name}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              />
            </Field>
            <Field label="Locale">
              <input
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-400"
                value={form.locale}
                onChange={(e) => setForm({ ...form, locale: e.target.value })}
              />
            </Field>
            <Field label="Timezone">
              <input
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-400"
                value={form.timezone}
                onChange={(e) => setForm({ ...form, timezone: e.target.value })}
              />
            </Field>
          </div>
          <button
            onClick={saveProfile}
            disabled={savingProfile}
            className="mt-2 inline-flex items-center px-4 py-1.5 rounded-full bg-cyan-400 text-slate-900 text-sm font-semibold disabled:opacity-60"
          >
            {savingProfile ? 'Saving…' : 'Save profile'}
          </button>
        </section>

        {/* Password */}
        <section className="border border-slate-800 rounded-2xl bg-slate-900/70 px-4 py-4 space-y-3">
          <div className="text-sm font-medium mb-1">Password</div>
          <div className="space-y-2 text-sm">
            <Field label="Current password">
              <input
                type="password"
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-400"
                value={pwForm.currentPassword}
                onChange={(e) =>
                  setPwForm({ ...pwForm, currentPassword: e.target.value })
                }
              />
            </Field>
            <Field label="New password">
              <input
                type="password"
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-400"
                value={pwForm.newPassword}
                onChange={(e) =>
                  setPwForm({ ...pwForm, newPassword: e.target.value })
                }
              />
            </Field>
          </div>
          <button
            onClick={changePassword}
            disabled={changingPw}
            className="mt-2 inline-flex items-center px-4 py-1.5 rounded-full border border-slate-600 text-slate-100 text-sm hover:border-cyan-400 hover:text-cyan-200 disabled:opacity-60"
          >
            {changingPw ? 'Changing…' : 'Change password'}
          </button>
        </section>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-xs text-slate-400 mb-0.5">{label}</div>
      {children}
    </div>
  );
}
